import { parseFileEnvelopes } from '@runloop/reflex-contract';
import {
  isUserPromptEvent,
  parsePromptSource,
  type AxonEvent,
  type PromptVia,
} from '@reflex/shared';
import { isCodexProtocolEventType } from '@runloop/remote-agents-sdk/codex';
import type {
  ClassifiedEvent,
  TimelineKind,
  AgentConfigItem,
  AgentLifecycleItem,
  AgentStartedPayload,
  UserAttachment,
} from './types.js';

// System event types matching the SDK's SYSTEM_EVENT_TYPES
const SYSTEM_EVENT_TYPES = new Set([
  'turn.started',
  'turn.completed',
  'turn.cancelled',
  'turn.resumed',
  'background.changed',
  'broker.error',
  'devbox.running',
  'devbox.suspended',
  'devbox.shutdown',
  'devbox.failed',
  'agent.error',
  // Flow integration: published by `FlowEngine` when an agent runs as a
  // flow node so the chat can show "Started from automation" / "Returned to automation"
  // banners with deep-links back to the originating run-node.
  'agent.source.flow',
  'agent.flow_result',
  // Sandbox monitoring bridge: passive informational banners. A daemon
  // registered on-box (`reflex-service register --port`) and a sustained
  // CPU/memory warning. Both are system-origin and must NOT end the agent's
  // turn — they can fire while the agent is mid-work.
  'agent.daemon_started',
  'agent.performance_warning',
  // An agent asked for a capability (a watch, an interrupt, a destructive
  // action) and a person answered it in this thread. Passive: the agent is
  // not waiting on the card, so it never ends the turn.
  'agent.capability_requested',
  'agent.capability_resolved',
  // A structured work assessment the agent published via `reflex-service
  // assessment`. System-origin and passive: it announces a document the user
  // opens in the Assessment tab, and never ends the agent's turn.
  'agent.work_assessment',
  // Claude Max in-session credential refresh: reflex handed the box a fresh
  // access token (refreshed) or could not (refresh_failed). Passive
  // system-origin banners that never end the agent's turn.
  'agent.credentials_refreshed',
  'agent.credentials_refresh_failed',
  // A runtime model switch. Listed by type because it reaches the client with
  // an `AGENT_EVENT` origin.
  'agent.runtime_settings_changed',
  // Pull-request lifecycle: opened (registered via `reflex-service pr`), then
  // GitHub-webhook-driven merge / close / failed-checks / review. Passive
  // informational banners — merge/close end the agent via a separate
  // `agent.complete`, so these never flip the turn off themselves.
  'agent.pr_created',
  'agent.pr_merged',
  'agent.pr_closed',
  'agent.pr_checks_failed',
  'agent.pr_review',
]);

/**
 * `thread/tokenUsage/updated` is a valid Codex app-server notification, but
 * the SDK's classified-method allow-list (`isCodexProtocolEventType`) does not
 * include it. Recognized explicitly so token-usage frames still route to the
 * codex handler instead of falling through as `unknown`.
 */
export const CODEX_TOKEN_USAGE_EVENT_TYPE = 'thread/tokenUsage/updated';

/**
 * `codex.command.result` is a reflex-domain durable event (not a JSON-RPC
 * frame) published by the Codex server provider after handling a slash
 * command. Recognized explicitly so it routes to the codex handler, which
 * renders it as a system-note card.
 */
export const CODEX_COMMAND_RESULT_EVENT_TYPE = 'codex.command.result';

/**
 * `thread/start` is the outbound client *request* frame, not a notification,
 * so the SDK's classified-method allow-list does not include it. Routed to
 * the codex handler explicitly: its JSON-RPC id is the only way to identify
 * the metadata-rich `thread/start` response that seeds the session-info
 * (`system_init`) block.
 */
export const CODEX_THREAD_START_EVENT_TYPE = 'thread/start';

function isCodexEventType(eventType: string): boolean {
  return (
    isCodexProtocolEventType(eventType) ||
    eventType === CODEX_TOKEN_USAGE_EVENT_TYPE ||
    eventType === CODEX_COMMAND_RESULT_EVENT_TYPE ||
    eventType === CODEX_THREAD_START_EVENT_TYPE
  );
}

// Claude protocol event types (sent by the Claude adapter)
const CLAUDE_PROTOCOL_TYPES = new Set([
  'system',
  'user',
  'assistant',
  'result',
  'error',
  'rate_limit_event',
  'control_request',
  'control_response',
  'query',
  'controlRequest',
]);

// ACP protocol event types (sent by the ACP adapter)
const ACP_PROTOCOL_TYPES = new Set([
  'initialize',
  'session/new',
  'session/prompt',
  'session/cancel',
  'session/update',
  'user_message_chunk',
  'agent_message_chunk',
  'agent_thought_chunk',
  'tool_call',
  'tool_call_update',
  'plan',
  'available_commands_update',
  'current_mode_update',
  'config_option_update',
  'session_info_update',
  'usage_update',
]);

/**
 * Safely extract the event payload as an object.
 * Handles both pre-parsed objects and JSON string payloads (which can occur
 * when events flow through Axon SSE or REST serialization boundaries).
 */
function parsePayload(raw: unknown): Record<string, unknown> {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // not valid JSON — fall through
    }
  }
  return {};
}

/**
 * Classify an AxonEvent into a timeline kind, mirroring the SDK's
 * `createClassifier()` logic but operating on reflex's AxonEvent type.
 */
export function classifyEvent(event: AxonEvent): ClassifiedEvent {
  const eventType = event.type;
  const payload = parsePayload(event.payload);

  // System events are identified by origin
  if (event.origin === 'SYSTEM_EVENT' || SYSTEM_EVENT_TYPES.has(eventType)) {
    return { kind: 'system', data: { type: eventType, ...payload }, originalEvent: event };
  }

  // Claude SDK bridge events use `turn.claude.*` prefixed types.
  // Strip the prefix and use the inner type for dispatch.
  if (eventType.startsWith('turn.claude.')) {
    const innerType = eventType.slice('turn.claude.'.length);
    const mergedData = { type: innerType, ...payload };
    // The payload may also carry its own `type` field (e.g. `assistant`) —
    // prefer the inner type derived from the event type prefix.
    if (payload.type && payload.type !== innerType) {
      mergedData.type = innerType;
    }
    return { kind: 'claude_protocol', data: mergedData, originalEvent: event };
  }

  // `error` exists in both the Claude and Codex protocols. Native Codex
  // frames retain their JSON-RPC method in the durable payload, which lets us
  // disambiguate without stealing unprefixed Claude error events.
  if (isCodexEventType(eventType) && (eventType !== 'error' || payload.method === 'error')) {
    return { kind: 'codex_protocol', data: { type: eventType, ...payload }, originalEvent: event };
  }

  // Claude protocol events (unprefixed)
  if (CLAUDE_PROTOCOL_TYPES.has(eventType)) {
    return { kind: 'claude_protocol', data: { type: eventType, ...payload }, originalEvent: event };
  }

  // ACP protocol events
  if (ACP_PROTOCOL_TYPES.has(eventType)) {
    return { kind: 'acp_protocol', data: { type: eventType, ...payload }, originalEvent: event };
  }

  return { kind: 'unknown', data: payload, originalEvent: event };
}

/**
 * Determine the protocol kind from a list of events.
 * Used for auto-detection when agent type is not available.
 */
export function detectProtocol(events: AxonEvent[]): TimelineKind | null {
  for (const event of events) {
    const payload = parsePayload(event.payload);
    if (isCodexEventType(event.type) && (event.type !== 'error' || payload.method === 'error')) {
      return 'codex_protocol';
    }
    if (CLAUDE_PROTOCOL_TYPES.has(event.type)) return 'claude_protocol';
    if (ACP_PROTOCOL_TYPES.has(event.type)) return 'acp_protocol';
  }
  return null;
}

/**
 * Concatenate every text-block in a user message payload.
 * Handles both Claude (`message.content[]`) and ACP (`prompt[]`) shapes,
 * plus the legacy string variants. Used by {@link extractUserText} and
 * {@link extractFileEnvelopeAttachments} so they see the same source text.
 */
function joinUserTextBlocks(payload: Record<string, unknown>): string {
  // Native Codex: { method: "turn/start", params: { input: UserInput[] } }
  const params = payload.params;
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    const input = (params as Record<string, unknown>).input;
    if (Array.isArray(input)) {
      return input
        .filter(
          (block): block is Record<string, unknown> =>
            block != null &&
            typeof block === 'object' &&
            !Array.isArray(block) &&
            (block as Record<string, unknown>).type === 'text' &&
            typeof (block as Record<string, unknown>).text === 'string',
        )
        .map((block) => block.text as string)
        .join('\n');
    }
  }

  // Claude: { message: { content: [{ type: "text", text: "..." }] } }
  const message = payload.message;
  if (typeof message === 'string') return message;
  if (message && typeof message === 'object') {
    const content = (message as Record<string, unknown>).content;
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter((b: Record<string, unknown>) => b.type === 'text' && typeof b.text === 'string')
        .map((b: Record<string, unknown>) => b.text as string)
        .join('\n');
    }
  }

  // ACP: { prompt: [{ type: "text", text: "..." }] } or { prompt: "..." }
  const prompt = payload.prompt;
  if (typeof prompt === 'string') return prompt;
  if (Array.isArray(prompt)) {
    return prompt
      .filter((p: Record<string, unknown>) => typeof p.text === 'string')
      .map((p: Record<string, unknown>) => p.text as string)
      .join('\n');
  }

  if (typeof payload.text === 'string') return payload.text;
  return '';
}

/**
 * Extract user message text from an event payload, with any file-envelope
 * blocks (see `formatFileEnvelope`) stripped out. The agent providers
 * inline non-image, non-PDF attachments as `[File: ...]\n<content>\n[End of
 * file: ...]` text blocks for the model; this function returns just the
 * user's typed prose so the UI doesn't redundantly re-render the inlined
 * file content. The chip + content for those attachments comes from
 * {@link extractFileEnvelopeAttachments}.
 *
 * Handles both Claude and ACP message formats.
 */
export function extractUserText(payload: Record<string, unknown>): string {
  const raw = joinUserTextBlocks(payload);
  if (!raw) return '';
  return parseFileEnvelopes(raw).cleanedText;
}

/**
 * Extract attachment chips for any file-envelopes embedded in a user
 * message payload. Each chip is a `UserAttachment` with `type: 'file'`,
 * the original filename + MIME, and the inlined text content (which the UI
 * can show on demand without re-running the model).
 */
export function extractFileEnvelopeAttachments(payload: Record<string, unknown>): UserAttachment[] {
  const raw = joinUserTextBlocks(payload);
  if (!raw) return [];
  const { files } = parseFileEnvelopes(raw);
  return files.map(
    (f): UserAttachment => ({
      type: 'file',
      name: f.name,
      mimeType: f.mimeType,
      text: f.content,
    }),
  );
}

/**
 * Parse a `data:<mime>;base64,<payload>` URL into its MIME type and raw base64
 * data. Native Codex echoes image inputs as data URLs (its provider builds
 * `data:${mimeType};base64,${data}` in `toCodexInput`), so this recovers the
 * `{ data, mimeType }` shape the rest of the attachment pipeline expects.
 *
 * Returns `null` for anything that is not a base64 data URL (e.g. a remote
 * `https://` image reference), which carries no inline bytes to render.
 */
function parseImageDataUrl(url: string): { data: string; mimeType: string } | null {
  if (!url.startsWith('data:')) return null;
  const commaIndex = url.indexOf(',');
  if (commaIndex === -1) return null;
  const header = url.slice('data:'.length, commaIndex);
  if (!header.endsWith(';base64')) return null;
  const mimeType = header.slice(0, -';base64'.length);
  const data = url.slice(commaIndex + 1);
  if (!mimeType || !data) return null;
  return { data, mimeType };
}

/**
 * Extract image attachments from user message content blocks. Handles every
 * inline-image shape the agent protocols echo back:
 *   - Anthropic (Claude): `{ source: { type: 'base64', media_type, data } }`
 *   - Flat (ACP/OpenCode): `{ data, mimeType }`
 *   - Data URL (native Codex): `{ url: 'data:<mime>;base64,<data>' }`
 */
export function extractImageAttachments(content: unknown[]): UserAttachment[] {
  const attachments: UserAttachment[] = [];
  for (const block of content) {
    if (block == null || typeof block !== 'object' || !('type' in block)) continue;
    const b = block as Record<string, unknown>;
    if (b.type !== 'image') continue;

    // Anthropic-style: { source: { type: "base64", media_type, data } }
    const src = b.source as Record<string, unknown> | undefined;
    if (
      src?.type === 'base64' &&
      typeof src.data === 'string' &&
      typeof src.media_type === 'string'
    ) {
      attachments.push({ type: 'image', data: src.data, mimeType: src.media_type });
    } else if (typeof b.data === 'string' && typeof b.mimeType === 'string') {
      // Flat style (ACP/OpenCode): { type: 'image', mimeType, data }
      attachments.push({ type: 'image', data: b.data, mimeType: b.mimeType });
    } else if (typeof b.url === 'string') {
      // Data-URL style (native Codex): { type: 'image', url: 'data:...' }
      const parsed = parseImageDataUrl(b.url);
      if (parsed) attachments.push({ type: 'image', data: parsed.data, mimeType: parsed.mimeType });
    }
  }
  return attachments;
}

/**
 * Select the raw content-block array from a user-message payload across every
 * supported agent protocol. Mirrors {@link joinUserTextBlocks}' protocol
 * dispatch so images are read from the same source blocks as the text:
 *   - Native Codex (`turn/start` / `turn/steer`): `params.input`
 *   - Claude (`query`): `message.content` (or top-level `content`)
 *   - ACP/OpenCode (`session/prompt`): `prompt`
 *
 * Returns `[]` for the legacy string-only shapes (no structured blocks).
 */
function selectUserContentBlocks(payload: Record<string, unknown>): unknown[] {
  const params = payload.params;
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    const input = (params as Record<string, unknown>).input;
    if (Array.isArray(input)) return input;
  }

  const message = payload.message;
  if (message && typeof message === 'object' && !Array.isArray(message)) {
    const content = (message as Record<string, unknown>).content;
    if (Array.isArray(content)) return content;
  }
  if (Array.isArray(payload.content)) return payload.content;

  if (Array.isArray(payload.prompt)) return payload.prompt;

  return [];
}

/**
 * Extract image attachments from a user-message event payload, resolving the
 * correct content-block location per protocol. This is the payload-level entry
 * point used by the stream reducer; {@link extractImageAttachments} scans an
 * already-selected block array.
 *
 * Codex and ACP echo their prompts in `params.input` / `prompt` rather than
 * Claude's `message.content`, so reading only `message.content` dropped their
 * images from the confirmed transcript even though the optimistic bubble had
 * shown them — the image appeared to vanish the moment the echo landed.
 */
export function extractUserImageAttachments(payload: Record<string, unknown>): UserAttachment[] {
  return extractImageAttachments(selectUserContentBlocks(payload));
}

/**
 * Build an AgentConfigItem from an `agent_started` custom event.
 * Returns null if the event is not an agent_started event.
 */
export function buildAgentConfigItem(event: AxonEvent): AgentConfigItem | null {
  const eventType = event.type as string;
  if (eventType !== 'agent_started' && eventType !== 'agent.started') return null;

  const payload = event.payload as Record<string, unknown> | null;
  if (!payload) return null;

  const agentType = payload.agentType as string | undefined;
  const agentId = payload.agentId as string | undefined;
  if (!agentType || !agentId) return null;

  const config: AgentStartedPayload = {
    agentType,
    agentId,
    model: payload.model as string | undefined,
    agentBinary: payload.agentBinary as string | undefined,
    blueprintName: payload.blueprintName as string | undefined,
    launchArgs: payload.launchArgs as string[] | undefined,
    systemPrompt: payload.systemPrompt as string | undefined,
    ...payload,
  };

  return {
    id: `config-${event.id}`,
    role: 'system',
    itemType: 'agent_started',
    config,
  };
}

const AGENT_LIFECYCLE_LABELS: Record<string, string> = {
  'agent.interrupted': 'Agent interrupted',
  'agent.stopped': 'Agent stopped',
  'agent.killed': 'Agent killed',
  'agent.complete': 'Agent complete',
  'agent.error': 'Agent error',
  'devbox.running': 'Devbox running',
  'devbox.suspended': 'Devbox suspended',
  'devbox.shutdown': 'Devbox shutdown',
  'devbox.failed': 'Devbox failed',
  'agent.source.flow': 'Started from automation',
  'agent.flow_result': 'Returned to automation',
  'agent.daemon_started': 'Daemon started',
  'agent.capability_requested': 'Approval requested',
  'agent.capability_resolved': 'Approval answered',
  'agent.work_assessment': 'Work assessment',
  'agent.performance_warning': 'Performance warning',
  'agent.credentials_refreshed': 'Claude credentials refreshed',
  'agent.credentials_refresh_failed': 'Claude credentials expired, reconnect your subscription',
  // Fallback only. The renderer names the model from `AgentLifecycleItem.toModel`.
  'agent.runtime_settings_changed': 'Model switched',
  'agent.pr_created': 'Pull request opened',
  'agent.pr_merged': 'Pull request merged',
  'agent.pr_closed': 'Pull request closed',
  'agent.pr_checks_failed': 'Checks failed',
  'agent.pr_review': 'Pull request review',
  // Broker stdout/stderr log lines; rendered as muted log text (agent.stderr).
  'agent.log': 'Log',
};

/**
 * Build an AgentLifecycleItem from lifecycle events (interrupted, stopped, etc.).
 * Returns null if the event is not a recognised lifecycle event.
 */
export function buildAgentLifecycleItem(event: AxonEvent): AgentLifecycleItem | null {
  const label = AGENT_LIFECYCLE_LABELS[event.type];
  if (!label) return null;

  const item: AgentLifecycleItem = {
    id: `lifecycle-${event.id}`,
    role: 'system',
    itemType: 'agent_lifecycle',
    eventType: event.type,
    label,
    timestamp: event.timestamp,
  };

  const payload = (
    typeof event.payload === 'object' && event.payload !== null ? event.payload : {}
  ) as Record<string, unknown>;

  if (event.type === 'agent.error') {
    if (typeof payload.title === 'string') {
      item.title = payload.title;
    }
    if (typeof payload.error === 'string') {
      item.errorMessage = payload.error;
    } else if (typeof payload.message === 'string') {
      item.errorMessage = payload.message;
    }
  }

  if (event.type === 'devbox.failed') {
    if (typeof payload.reason === 'string') {
      item.errorMessage = payload.reason;
    }
  }

  // Both error events may carry raw failure logs (launch command output,
  // devbox platform logs) for a collapsible block in the error card.
  if (
    (event.type === 'agent.error' || event.type === 'devbox.failed') &&
    typeof payload.logs === 'string' &&
    payload.logs.trim().length > 0
  ) {
    item.logs = payload.logs;
  }

  if (event.type === 'agent.runtime_settings_changed') {
    if (typeof payload.toModel === 'string' && payload.toModel) {
      item.toModel = payload.toModel;
    }
    item.fromModel = typeof payload.fromModel === 'string' ? payload.fromModel : null;
    if (typeof payload.userId === 'string' && payload.userId) {
      item.actorUserId = payload.userId;
    }
  }

  return item;
}

/**
 * Check if an event represents a user message.
 * Matches protocol-specific user events (Claude `query`, ACP `session/prompt`,
 * and native Codex `turn/start`).
 */
export function isUserMessageEvent(event: AxonEvent): boolean {
  return isUserPromptEvent(event);
}

/**
 * Attribution helpers over the Axon `source` of a user-prompt event. The
 * wire format and its parser live in `@reflex/shared`; these are the two
 * questions the chat pane asks of it.
 *
 * `extractAuthorIdFromSource` returns `null` for unattributed events (bare
 * `reflex` source, empty / missing, or a source this host did not author) —
 * the caller renders those as "Unknown user".
 */
export function extractAuthorIdFromSource(source: string | undefined | null): string | null {
  return parsePromptSource(source)?.userId ?? null;
}

/**
 * The agent that sent the prompt on the author's behalf (Chief), or `null`
 * when the author sent it themselves.
 */
export function extractPromptViaFromSource(source: string | undefined | null): PromptVia | null {
  return parsePromptSource(source)?.via ?? null;
}
